import { SIGNAL_NAMES, signalFilled, type ConciergeSignals, type SignalName } from './conciergeState'

export type RecommendationReadiness = {
  ready: boolean
  filledCount: number
  reason: string
}

// We hold the first fetch until we have two details, and at least one that says
// who it's for or what they're into — a location or budget alone can't seed a
// search. Two pieces keep the results from being uselessly broad.
export function recommendationReadiness (signals: ConciergeSignals): RecommendationReadiness {
  const hasTargeting = signalFilled(signals.relationship)
    || signalFilled(signals.occasion)
    || signalFilled(signals.interests)

  const filledCount = SIGNAL_NAMES.filter(name => signalFilled(signals[name])).length
  const ready = hasTargeting && filledCount >= 2

  return {
    ready,
    filledCount,
    reason: ready
      ? 'Enough to start finding gifts.'
      : hasTargeting
        ? 'Add one more detail to start.'
        : "Tell us who it's for or what they're into.",
  }
}

// The next detail to nudge for, as a prompt — the top useful one still missing. Keeps
// guiding the buyer toward a fuller sentence even after there's technically enough to
// search, so the hint doesn't just vanish. Budget is omitted (we don't nudge a price);
// returns null only once every useful signal is captured.
const NEXT_HINTS: Array<{ name: SignalName, hint: string }> = [
  { name: 'relationship', hint: "Who's it for?" },
  { name: 'occasion', hint: "What's the occasion?" },
  { name: 'interests', hint: 'What are they into?' },
  { name: 'location', hint: 'Where do they live? (for local picks)' },
]

export function nextSignalHint (signals: ConciergeSignals): string | null {
  const hasTargeting = signalFilled(signals.relationship)
    || signalFilled(signals.occasion)
    || signalFilled(signals.interests)
  if (!hasTargeting) return "Tell us who it's for or what they're into."

  return NEXT_HINTS.find(({ name }) => !signalFilled(signals[name]))?.hint ?? null
}
